import { useCallback, useRef, useState, useEffect } from "react"

interface SliderProps {
  value: number
  onValueChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  className?: string
  id?: string
}

/**
 * Slider 滑块组件
 * 用于在范围内选择数值
 */
export function Slider({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  className = "",
  id,
}: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  
  // 使用 ref 存储回调，避免 useEffect 依赖变化
  const onValueChangeRef = useRef(onValueChange)
  onValueChangeRef.current = onValueChange

  // 计算百分比位置
  const percentage = ((value - min) / (max - min)) * 100

  // 根据位置计算值（不依赖 value，避免拖动时频繁重新创建）
  const calculateValue = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return min
      const rect = trackRef.current.getBoundingClientRect()
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      const rawValue = min + percent * (max - min)
      // 按步长对齐
      const steppedValue = Math.round(rawValue / step) * step
      return Math.max(min, Math.min(max, steppedValue))
    },
    [min, max, step]
  )

  // 处理点击和开始拖动
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return
      e.preventDefault() // 防止选中文本
      setIsDragging(true)
      const newValue = calculateValue(e.clientX)
      onValueChangeRef.current(newValue)
    },
    [disabled, calculateValue]
  )

  // 拖动事件处理
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      const newValue = calculateValue(e.clientX)
      onValueChangeRef.current(newValue)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    // 添加事件监听器到 document，确保鼠标移出滑块区域也能继续拖动
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, calculateValue])

  // 支持触摸设备
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return
      setIsDragging(true)
      const touch = e.touches[0]
      const newValue = calculateValue(touch.clientX)
      onValueChangeRef.current(newValue)
    },
    [disabled, calculateValue]
  )

  useEffect(() => {
    if (!isDragging) return

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      const newValue = calculateValue(touch.clientX)
      onValueChangeRef.current(newValue)
    }

    const handleTouchEnd = () => {
      setIsDragging(false)
    }

    document.addEventListener("touchmove", handleTouchMove, { passive: true })
    document.addEventListener("touchend", handleTouchEnd)

    return () => {
      document.removeEventListener("touchmove", handleTouchMove)
      document.removeEventListener("touchend", handleTouchEnd)
    }
  }, [isDragging, calculateValue])

  return (
    <div
      id={id}
      ref={trackRef}
      role="slider"
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-disabled={disabled}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={`
        relative flex h-5 w-full touch-none select-none items-center
        ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
        ${className}
      `}
    >
      {/* 轨道 */}
      <div className="relative h-1.5 w-full rounded-full bg-slate-200">
        {/* 已填充部分 */}
        <div
          className="absolute h-full rounded-full bg-primary"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {/* 滑块手柄 - 增大点击区域 */}
      <div
        className={`
          absolute h-5 w-5 -ml-2.5 rounded-full cursor-grab
          flex items-center justify-center
          ${isDragging ? "cursor-grabbing" : ""}
        `}
        style={{ left: `${percentage}%` }}
      >
        <div
          className={`
            h-4 w-4 rounded-full border-2 border-primary bg-background shadow
            transition-shadow
            ${isDragging ? "ring-2 ring-primary/20 scale-110" : "hover:scale-105"}
          `}
        />
      </div>
    </div>
  )
}
